import { API_ROUTES } from '@/constants/apiRoutes';
import {
  useDelete,
  useGetPaged,
  useGet,
  usePatch,
  usePost,
  usePut,
  unpackMappedResponse,
  unpackMappedResponseWithKey
} from '../ApiService';
import { getAccessToken, getRefreshToken } from '../../utils/cookieHelpers';
import type {
  User,
  UserCredentials,
  GetUsersRequest,
  GetUserInfo,
  GetUserPermissions,
  UserPermission,
  LoginResponse,
  CreateUserWithRolesAndClaims,
  ImportUserWithRolesAndClaims,
  HasChildOrganizations,
  ImportedUser,
  ImportUsersResponse,
  ImportUsersRequest,
  MergedAccountResponse,
  UserWithUpdatedPassword
} from '@/services/security/models/user';
import { decodeAccessToken, isAccessTokenExpired } from '../../utils/jwtHelpers';
import type {
  ApproveRegistration,
  EditRegistration,
  GetUserRegistrationInfo,
  Registration,
  RegistrationInfo,
  RegistrationInfoPayload,
  RegistrationsRequest,
  UserRegistrationForm,
  UserRegistrationInfo,
  UserRegistrationResponse
} from './models/registration';
import type {
  EditExplicitHierarchyRespone,
  ExplicitHierarchiesRequest,
  ExplicitHierarchy,
  ExplicitHierarchyForm
} from './models/explicitHierarchy';
import type {
  ChildOrganisationsResponse,
  GetChildOrganizationsRequest,
  Organization
} from './models/organization';
import type {
  EditReportDocument,
  ReportDocumentResponse,
  ReportDetails,
  ReportDetailsData,
  ReportDetailsGet,
  UploadReportFile
} from './models/report';
import { RequestStatus, type ApiResponse, type MappedResponse } from '@/core/api/types';
import type { AvatarData, AvatarResponse, UploadAvatarRequest } from './models';
import type { ReportFile } from '../report/models';
import { RequestHeaders } from '@/constants/requestHeaders';

export async function login(credentials: UserCredentials): Promise<MappedResponse<LoginResponse>> {
  const response = await usePost<LoginResponse, UserCredentials>(
    API_ROUTES.Security.Login,
    credentials
  );

  return unpackMappedResponse(response);
}

export async function logoutUser(): Promise<MappedResponse<number>> {
  const accessToken = getAccessToken();
  const refreshToken = getRefreshToken();
  if (!accessToken && !refreshToken) {
    return { status: RequestStatus.ERROR, data: null };
  }
  const accessTokenExpired = isAccessTokenExpired(decodeAccessToken(accessToken)?.exp || 0);
  if (accessTokenExpired) {
    return { status: RequestStatus.ERROR, data: null };
  }
  const result = await useDelete<number>(API_ROUTES.Security.Login);
  return unpackMappedResponse(result);
}

export async function sendResetPasswordLink(login: string): Promise<MappedResponse<User>> {
  const result = await usePost<User, { login: string }>(API_ROUTES.Security.SendResetPasswordLink, {
    login
  });
  return unpackMappedResponse(result);
}

export async function activateUser(login: string): Promise<MappedResponse<User>> {
  const result = await usePost<User, { login: string }>(API_ROUTES.Security.ActivateUser, {
    login
  });
  return unpackMappedResponse(result);
}

export async function retrieveRegistrationRequest(
  registrationToken: string
): Promise<MappedResponse<RegistrationInfo>> {
  const response = await usePost<{ registration: RegistrationInfo }, { registrationToken: string }>(
    API_ROUTES.Security.RetrieveRegistrationRequest,
    { registrationToken }
  );
  return unpackMappedResponseWithKey(response, 'registration');
}

export async function registerEntity(
  registration: UserRegistrationForm
): Promise<MappedResponse<UserRegistrationResponse>> {
  const result = await usePost<UserRegistrationResponse, UserRegistrationForm>(
    API_ROUTES.Security.RegisterEntity,
    registration
  );
  return unpackMappedResponse(result);
}

export async function refreshAccessToken(token: string): Promise<ApiResponse<LoginResponse>> {
  const response = await useGet<LoginResponse, UserCredentials>(
    API_ROUTES.Security.RefreshToken,
    undefined,
    {
      headers: {
        [RequestHeaders.Authorization]: `${RequestHeaders.Bearer} ${token}`
      }
    }
  );
  return response;
}

export async function getRegistrationInfo(): Promise<MappedResponse<RegistrationInfo>> {
  const response = await useGet<RegistrationInfoPayload>(API_ROUTES.Security.RegistrationInfo);
  return unpackMappedResponseWithKey(response, 'registration');
}

export async function hasChildOrganizations(): Promise<MappedResponse<HasChildOrganizations>> {
  const response = await useGet<HasChildOrganizations>(API_ROUTES.Security.HasChildOrganizations);
  return unpackMappedResponse(response);
}

export async function getUser(): Promise<MappedResponse<User>> {
  const response = await useGet<{ user: User }>(API_ROUTES.Security.GetUser);
  return unpackMappedResponseWithKey(response, 'user');
}

export async function createUser(
  user: CreateUserWithRolesAndClaims
): Promise<MappedResponse<number>> {
  const response = await usePost<number, CreateUserWithRolesAndClaims>(
    API_ROUTES.Security.CreateUser,
    user
  );
  return unpackMappedResponse(response);
}

export async function importUsers(
  users: ImportUserWithRolesAndClaims[]
): Promise<MappedResponse<ImportedUser[]>> {
  const response = await usePost<ImportUsersResponse, ImportUsersRequest>(
    API_ROUTES.Security.ImportUsers,
    { users }
  );
  return unpackMappedResponseWithKey(response, 'users');
}

export async function getMergedAccounts(): Promise<MappedResponse<User[]>> {
  const response = await useGet<User[]>(API_ROUTES.Security.GetMergedAccounts);
  return unpackMappedResponse(response);
}

export async function addMergedAccount(
  payload: UserCredentials
): Promise<MappedResponse<MergedAccountResponse>> {
  const response = await usePost<MergedAccountResponse, UserCredentials>(
    API_ROUTES.Security.GetMergedAccounts,
    payload
  );
  return unpackMappedResponse(response);
}

export async function deleteMergedAccount(
  login: string
): Promise<MappedResponse<MergedAccountResponse>> {
  const response = await useDelete<MergedAccountResponse>(API_ROUTES.Security.EditMergedAccount, {
    data: { login }
  });
  return unpackMappedResponse(response);
}

export async function loginWithMergedAccount(
  login: string
): Promise<MappedResponse<LoginResponse>> {
  const response = await usePost<LoginResponse, { login: string }>(
    API_ROUTES.Security.LoginWithMergedAccount,
    { login }
  );

  return unpackMappedResponse(response);
}

export async function getUsers(payload: GetUsersRequest): Promise<MappedResponse<User[]>> {
  const response = await useGetPaged<User[], GetUsersRequest>(
    API_ROUTES.Security.GetUsers,
    payload
  );
  return unpackMappedResponse(response);
}

export async function getExplicitHierarchies(
  payload: ExplicitHierarchiesRequest
): Promise<MappedResponse<ExplicitHierarchy[]>> {
  const response = await useGetPaged<ExplicitHierarchy[], ExplicitHierarchiesRequest>(
    API_ROUTES.Security.GetExplicitiHierarchies,
    payload
  );
  return unpackMappedResponse(response);
}

export async function addExplicitiHierarchies(
  payload: ExplicitHierarchyForm
): Promise<MappedResponse<EditExplicitHierarchyRespone[]>> {
  const response = await usePost<EditExplicitHierarchyRespone[], ExplicitHierarchyForm>(
    API_ROUTES.Security.AddExplicitiHierarchies,
    payload
  );
  return unpackMappedResponse(response);
}

export async function removeExplicitiHierarchies(
  data: ExplicitHierarchyForm
): Promise<MappedResponse<EditExplicitHierarchyRespone[]>> {
  const response = await useDelete<EditExplicitHierarchyRespone[]>(
    API_ROUTES.Security.RemoveExplicitiHierarchies,
    { data }
  );
  return unpackMappedResponse(response);
}

export async function getRegistrations(
  payload: RegistrationsRequest
): Promise<MappedResponse<Registration[]>> {
  const response = await useGetPaged<Registration[], RegistrationsRequest>(
    'security/registrations',
    payload
  );
  return unpackMappedResponse(response);
}

export async function editUserRegistration(
  registrationData: EditRegistration
): Promise<MappedResponse<null>> {
  const response = await usePut<null, EditRegistration>(
    API_ROUTES.Security.EditRegistration,
    registrationData
  );
  return unpackMappedResponse(response);
}

export async function getReportDetails(): Promise<MappedResponse<ReportDetails>> {
  const response = await useGet<ReportDetailsGet>(API_ROUTES.Security.ReportDetails);
  return unpackMappedResponseWithKey(response, 'reportDetails');
}

export async function setReportDetails(data: ReportDetailsData): Promise<MappedResponse<void>> {
  const result = await usePut<void, ReportDetailsData>(API_ROUTES.Security.ReportDetails, data);
  return unpackMappedResponse(result);
}

export async function getUserRegistrationInfo(
  userId: number
): Promise<MappedResponse<UserRegistrationInfo>> {
  const response = await useGet<GetUserRegistrationInfo>(
    API_ROUTES.Security.GetUserRegistrationInfo(userId)
  );
  return unpackMappedResponseWithKey(
    response,
    'registration'
  ) as MappedResponse<UserRegistrationInfo>;
}

export async function approveUserRegistration(
  data: ApproveRegistration
): Promise<MappedResponse<number>> {
  const result = await usePost<number, ApproveRegistration>(
    API_ROUTES.Security.ApproveUserRegistraition,
    data
  );
  return unpackMappedResponse(result);
}

export async function cancelUserRegistration(
  registration: UserRegistrationInfo
): Promise<MappedResponse<number>> {
  const result = await usePost<number, UserRegistrationInfo>(
    API_ROUTES.Security.CancelUserRegistration,
    registration
  );
  return unpackMappedResponse(result);
}

export async function getUserInfo(userId: number): Promise<MappedResponse<User>> {
  const response = await useGet<GetUserInfo>(API_ROUTES.Security.GetUserInfo(userId));
  return unpackMappedResponseWithKey(response, 'user');
}

export async function getPermissions(): Promise<UserPermission[]> {
  const response = await useGet<GetUserPermissions>(API_ROUTES.Security.GetPermissions);
  return response.data?.claims || [];
}

export async function getAvatarId(): Promise<MappedResponse<string | null>> {
  const response = await useGet<{ avatar: string | null }>(API_ROUTES.Security.GetAvatarId);
  return unpackMappedResponseWithKey(response, 'avatar');
}

export async function uploadReportFile(
  data: UploadReportFile
): Promise<MappedResponse<ReportFile>> {
  if (!data.file) {
    return {
      status: RequestStatus.ERROR,
      data: null
    };
  }
  const formData = new FormData();
  formData.append('file', data.file);
  formData.append('reportName', data.reportName);
  formData.append('description', data.description);
  formData.append('title', data.title);
  formData.append('size', data.size + '');
  formData.append('extension', data.extension);
  const result = await usePost<ReportDocumentResponse, FormData>(
    API_ROUTES.Files.UploadReportFile,
    formData,
    {
      headers: { [RequestHeaders.ContentType]: 'multipart/form-data' }
    }
  );
  return unpackMappedResponseWithKey(result, 'file');
}

export async function editReportDocument(
  data: EditReportDocument
): Promise<MappedResponse<ReportFile>> {
  const result = await usePut<ReportDocumentResponse, EditReportDocument>(
    API_ROUTES.Files.UpdateReportFile(data.fileId),
    data
  );
  return unpackMappedResponseWithKey(result, 'file');
}

export async function updateUser(user: User): Promise<MappedResponse<number>> {
  const result = await usePut<number, User>(API_ROUTES.Security.UpdateUser(user.userId), user);
  return unpackMappedResponse(result);
}

export async function updateUserPassword(
  user: UserWithUpdatedPassword
): Promise<MappedResponse<number>> {
  const result = await usePatch<number, UserWithUpdatedPassword>(
    API_ROUTES.Security.UpdatePassword,
    user
  );
  return unpackMappedResponse(result);
}

export async function resetUserPassword(
  token: string,
  user: User
): Promise<MappedResponse<number>> {
  const result = await usePatch<number, User>(API_ROUTES.Security.ResetPassword(token), user);
  return unpackMappedResponse(result);
}

export async function activateUserAccount(
  token: string,
  user: User
): Promise<MappedResponse<number>> {
  const result = await usePatch<number, User>(API_ROUTES.Security.SetPasswordForActivation(token), user);
  return unpackMappedResponse(result);
}

export async function restoreUser(user: User): Promise<MappedResponse<User>> {
  const result = await usePost<[User], [User]>(API_ROUTES.Security.RestoreUser, [user]);
  const mappedResponse = unpackMappedResponse(result);
  return {
    status: mappedResponse.status,
    data: mappedResponse.data ? mappedResponse.data[0] : null
  } as MappedResponse<User>;
}

export async function disableUser(user: User): Promise<MappedResponse<User>> {
  const result = await usePost<[User], [User]>(API_ROUTES.Security.DisableUser, [user]);
  const mappedResponse = unpackMappedResponse(result);
  return {
    status: mappedResponse.status,
    data: mappedResponse.data ? mappedResponse.data[0] : null
  } as MappedResponse<User>;
}

export async function revokeUser(user: User): Promise<MappedResponse<User>> {
  const result = await usePost<[User], [User]>(API_ROUTES.Security.RevokeUser, [user]);
  const mappedResponse = unpackMappedResponse(result);
  return {
    status: mappedResponse.status,
    data: mappedResponse.data ? mappedResponse.data[0] : null
  } as MappedResponse<User>;
}

export async function uploadUserAvatar(file: string): Promise<MappedResponse<AvatarData>> {
  const user = await usePost<AvatarResponse, UploadAvatarRequest>(
    API_ROUTES.Security.EditUserAvatar,
    { file }
  );
  return unpackMappedResponseWithKey(user, 'avatar');
}

export async function deleteUserAvatar(): Promise<MappedResponse<AvatarData>> {
  const result = await useDelete<AvatarResponse>(API_ROUTES.Security.EditUserAvatar);
  return unpackMappedResponseWithKey(result, 'avatar');
}

export async function getChildOrganizations(
  params: GetChildOrganizationsRequest
): Promise<MappedResponse<Organization[]>> {
  const response = await useGetPaged<ChildOrganisationsResponse, GetChildOrganizationsRequest>(
    API_ROUTES.Security.GetOrganizations,
    params
  );
  return unpackMappedResponseWithKey(response, 'childOrganizations');
}

export async function validateResetPasswordToken(
  activationToken: string
): Promise<MappedResponse<User>> {
  const response = await usePost<{ user: User }, { activationToken: string }>(
    API_ROUTES.Security.ValidateResetPasswordToken,
    {
      activationToken
    }
  );
  return unpackMappedResponseWithKey(response, 'user');
}

export async function validateUserActivationToken(
  activationToken: string
): Promise<MappedResponse<User>> {
  const response = await usePost<{ user: User }, { activationToken: string }>(
    API_ROUTES.Security.ValidateActivationToken,
    {
      activationToken
    }
  );
  return unpackMappedResponseWithKey(response, 'user');
}

export async function updateUserPermissions(user: User, hasCreateClaim: boolean): Promise<MappedResponse<number>> {
  const url = hasCreateClaim ? API_ROUTES.Security.UpdateUserPermissions(user.userId) : API_ROUTES.Security.UpdateLocalUserPermissions(user.userId);
  const response = await usePut<number, User>(url, user);
  return unpackMappedResponse(response);
}
